import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, Award, FileText } from 'lucide-react';
import api from '../lib/api';
import { useCurrency } from '../hooks/useCurrency';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import MediaGallery from '../components/MediaGallery';
import ShareButtons from '../components/ShareButtons';
import SEOHead from '../components/SEOHead';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { formatPrice, loading: currencyLoading } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
    checkWishlist();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const checkWishlist = async () => {
    if (!isAuthenticated()) return;
    try {
      const response = await api.get('/wishlist');
      const wishlistItems = response.data.items || [];
      setIsInWishlist(wishlistItems.some(item => item.id === id));
    } catch (error) {
      console.error('Failed to check wishlist');
    }
  };

  const handleAddToCart = async () => {
    if (product.stock_status !== 'in_stock') {
      toast.error('This item is not available');
      return;
    }
    await addToCart(product.id);
  };

  const handleToggleWishlist = async () => {
    if (!isAuthenticated()) {
      toast.error('Please sign in to add to wishlist');
      navigate('/login');
      return;
    }

    try {
      if (isInWishlist) {
        await api.delete(`/wishlist/${product.id}`);
        setIsInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await api.post('/wishlist', { product_id: product.id });
        setIsInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!product) return null;

  // Helper function to convert relative URLs to absolute
  const getAbsoluteUrl = (url) => {
    if (!url) return 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/57qw7w56_F683E346-12CC-4A47-A850-08225E60D944.png';
    if (url.startsWith('http')) return url;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${backendUrl}${url}`;
  };

  // Prepare all media items
  const allImages = [product.main_image_url, ...(product.gallery_image_urls || [])].filter(Boolean);
  const allVideos = product.video_urls || [];

  const productUrl = window.location.href;
  const shareDescription = `Discover this exquisite ${product.carat_weight}ct ${product.gem_type} from ${product.origin || 'natural sources'}. ${product.description_long?.substring(0, 150) || 'A stunning gemstone from The Gem Atelier.'}`;
  const shareImage = getAbsoluteUrl(product.main_image_url || allImages[0]);
  const absoluteImageUrl = shareImage;

  return (
    <div className="min-h-screen py-12 px-4">
      <SEOHead
        title={`${product.name} - ${product.carat_weight}ct ${product.gem_type} | The Gem Atelier`}
        description={shareDescription}
        image={absoluteImageUrl}
        url={productUrl}
        type="product"
        productPrice={product.price}
        productCurrency={product.currency || 'USD'}
      />
      
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Media Gallery */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <MediaGallery
              images={allImages}
              videos={allVideos}
              productName={product.name}
            />
          </motion.div>

          {/* Details */}
          <div>
            <h1 className="font-serif text-4xl mb-4" data-testid="product-name">{product.name}</h1>
            
            <div className="mb-6">
              <p className="text-3xl font-light" data-testid="product-price">
                {currencyLoading ? (
                  <span className="text-gray-400">Loading price...</span>
                ) : product.price_on_request ? (
                  'Price on Request'
                ) : (
                  formatPrice(product.price, product.currency || 'USD')
                )}
              </p>
              <p className="text-sm text-gray-600 mt-1" data-testid="stock-status">
                {product.stock_status === 'in_stock' ? 'In Stock' : product.stock_status === 'reserved' ? 'Reserved' : 'Sold'}
              </p>
            </div>

            {/* Stock Status */}
            {product.stock_status === 'sold' && (
              <div className="bg-black text-white px-6 py-4 mb-6 text-center">
                <p className="font-bold text-2xl tracking-wider">SOLD</p>
                <p className="text-sm mt-1">This item has been purchased</p>
              </div>
            )}
            
            {product.stock_status === 'reserved' && (
              <div className="bg-yellow-500 text-black px-6 py-4 mb-6 text-center">
                <p className="font-bold text-lg">RESERVED</p>
                <p className="text-sm mt-1">Currently being purchased by another customer</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 mb-8">
              {product.stock_status === 'in_stock' && (
                <>
                  <Button
                    onClick={() => {
                      handleAddToCart();
                      navigate('/checkout');
                    }}
                    className="flex-1 bg-black text-white hover:bg-gray-900"
                    size="lg"
                    data-testid="buy-now-button"
                  >
                    Buy Now
                  </Button>
                  <Button
                    onClick={handleAddToCart}
                    variant="outline"
                    className="flex-1 border-black hover:bg-gray-100"
                    size="lg"
                    data-testid="add-to-cart-button"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                </>
              )}
              {product.stock_status !== 'sold' && (
                <Button
                  onClick={handleToggleWishlist}
                  variant="outline"
                  size="lg"
                  className={`border-black hover:bg-black hover:text-white ${isInWishlist ? 'bg-black text-white' : ''}`}
                  data-testid="wishlist-button"
                >
                  <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-current' : ''}`} />
                </Button>
              )}
            </div>

            {/* Share Section */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <ShareButtons product={product} url={productUrl} />
            </div>

            {/* Specifications */}
            <div className="border-t border-gray-200 pt-8 space-y-6">
              <div>
                <h2 className="font-serif text-2xl mb-4">Specifications</h2>
                <dl className="space-y-3 text-sm" data-testid="specifications">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Category:</dt>
                    <dd className="font-medium capitalize">{product.category}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Gem Type:</dt>
                    <dd className="font-medium capitalize">{product.gem_type}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Carat Weight:</dt>
                    <dd className="font-medium">{product.carat_weight} ct</dd>
                  </div>
                  {product.dimensions && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Dimensions:</dt>
                      <dd className="font-medium">{product.dimensions}</dd>
                    </div>
                  )}
                  {product.shape && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Shape:</dt>
                      <dd className="font-medium capitalize">{product.shape.replace('_', ' ')}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Color:</dt>
                    <dd className="font-medium">{product.color_description}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Clarity:</dt>
                    <dd className="font-medium">{product.clarity_description}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Origin:</dt>
                    <dd className="font-medium">{product.origin}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Treatment:</dt>
                    <dd className="font-medium">{product.treatment}</dd>
                  </div>
                  {product.certification_lab && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Certification:</dt>
                      <dd className="font-medium">{product.certification_lab} #{product.certification_id}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div>
                <h2 className="font-serif text-2xl mb-4">Description</h2>
                <p className="text-gray-700 leading-relaxed" data-testid="description">{product.description_long}</p>
              </div>

              {product.certification_file_url && (
                <div className="pt-6 border-t">
                  <Button variant="outline" className="border-black">
                    <FileText className="w-4 h-4 mr-2" />
                    View Certificate
                  </Button>
                </div>
              )}

              {/* Contact for Enquiry */}
              <div className="pt-6 border-t">
                <p className="text-sm text-gray-600 mb-3">Have questions about this gemstone?</p>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => window.open('https://wa.me/15551234567', '_blank')}
                    className="border-black hover:bg-black hover:text-white"
                  >
                    WhatsApp Enquiry
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/contact')}
                    className="border-black hover:bg-black hover:text-white"
                  >
                    Email Us
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
